import React from 'react';
import { useLocation } from 'react-router-dom';

interface SEOHeadProps {
  title?: string;
  description?: string;
  keywords?: string;
  ogTitle?: string;
  ogDescription?: string;
  ogImage?: string;
  ogImageAlt?: string;
  ogImageWidth?: string;
  ogImageHeight?: string;
  ogVideo?: string;
  ogVideoType?: string;
  ogVideoWidth?: string;
  ogVideoHeight?: string;
  ogUrl?: string;
  ogLocale?: string;
  ogLocaleAlternates?: string[];
  hreflangLinks?: Array<{ hreflang: string; href: string }>;
  twitterCard?: 'summary' | 'summary_large_image' | 'app' | 'player';
  twitterPlayer?: string;
  twitterPlayerWidth?: string;
  twitterPlayerHeight?: string;
  canonicalUrl?: string;
  structuredData?: object;
  noIndex?: boolean;
  noIndexFollow?: boolean;
  preloadImages?: Array<{ href: string; media?: string }>;
}

/**
 * SEOHead Component
 *
 * Reusable component for managing SEO meta tags dynamically per page.
 * Uses native React approach with useEffect to update document head.
 *
 * @param {SEOHeadProps} props - SEO configuration
 */
const SEOHead: React.FC<SEOHeadProps> = ({
  title,
  description,
  keywords,
  ogTitle,
  ogDescription,
  ogImage = 'https://chat.legeaaplicata.ro/brand/legea-aplicata-social.webp?v=20261010-monochrome-v1',
  ogImageAlt,
  ogImageWidth,
  ogImageHeight,
  ogVideo,
  ogVideoType,
  ogVideoWidth,
  ogVideoHeight,
  ogUrl,
  ogLocale,
  ogLocaleAlternates,
  hreflangLinks,
  twitterCard = 'summary_large_image',
  twitterPlayer,
  twitterPlayerWidth,
  twitterPlayerHeight,
  canonicalUrl,
  structuredData,
  noIndex = false,
  noIndexFollow = false,
  preloadImages,
}) => {
  const location = useLocation();

  React.useEffect(() => {
    const finalCanonicalUrl = canonicalUrl || `https://chat.legeaaplicata.ro${location.pathname}`;
    // Update document title
    if (title) {
      document.title = title;
    }

    // Helper function to update or create meta tag
    const updateMetaTag = (selector: string, attribute: string, value: string) => {
      let element = document.querySelector(selector);
      if (!element) {
        element = document.createElement('meta');
        if (selector.includes('property=')) {
          element.setAttribute('property', selector.match(/property="([^"]+)"/)?.[1] || '');
        } else {
          element.setAttribute('name', selector.match(/name="([^"]+)"/)?.[1] || '');
        }
        document.head.appendChild(element);
      }
      element.setAttribute(attribute, value);
    };

    // Update description
    if (description) {
      updateMetaTag('meta[name="description"]', 'content', description);
    }

    // Remove inherited keywords when a page intentionally omits them.
    if (keywords) {
      updateMetaTag('meta[name="keywords"]', 'content', keywords);
    } else {
      document.querySelectorAll('meta[name="keywords"]').forEach(element => element.remove());
    }

    // Update robots
    if (noIndex) {
      updateMetaTag('meta[name="robots"]', 'content', noIndexFollow ? 'noindex, follow' : 'noindex, nofollow');
    } else {
      updateMetaTag('meta[name="robots"]', 'content', 'index, follow, max-image-preview:large, max-snippet:-1, max-video-preview:-1');
    }

    // Update Open Graph tags
    updateMetaTag('meta[property="og:type"]', 'content', 'website');
    updateMetaTag('meta[property="og:url"]', 'content', ogUrl || finalCanonicalUrl);
    if (ogLocale) {
      updateMetaTag('meta[property="og:locale"]', 'content', ogLocale);
    }
    if (ogLocaleAlternates && ogLocaleAlternates.length > 0) {
      document.querySelectorAll('meta[property="og:locale:alternate"]').forEach((el) => el.remove());
      ogLocaleAlternates.forEach((alt) => {
        const altMeta = document.createElement('meta');
        altMeta.setAttribute('property', 'og:locale:alternate');
        altMeta.setAttribute('content', alt);
        altMeta.setAttribute('data-seo-alternate', 'true');
        document.head.appendChild(altMeta);
      });
    } else {
      document.querySelectorAll('meta[data-seo-alternate="true"]').forEach((el) => el.remove());
    }
    if (ogTitle || title) {
      updateMetaTag('meta[property="og:title"]', 'content', ogTitle || title!);
    }
    if (ogDescription || description) {
      updateMetaTag('meta[property="og:description"]', 'content', ogDescription || description!);
    }
    if (ogImage) {
      updateMetaTag('meta[property="og:image"]', 'content', ogImage);
      const imageExtension = new URL(ogImage, window.location.origin).pathname.split('.').pop()?.toLowerCase();
      const imageTypes: Record<string, string> = { jpg: 'image/jpeg', jpeg: 'image/jpeg', png: 'image/png', webp: 'image/webp', gif: 'image/gif', svg: 'image/svg+xml' };
      const imageType = imageTypes[imageExtension || ''];
      if (imageType) updateMetaTag('meta[property="og:image:type"]', 'content', imageType);
      else document.querySelectorAll('meta[property="og:image:type"]').forEach(element => element.remove());
    }
    for (const [property, value] of Object.entries({ 'og:image:alt': ogImageAlt, 'og:image:width': ogImageWidth, 'og:image:height': ogImageHeight })) {
      if (value) updateMetaTag(`meta[property="${property}"]`, 'content', value);
      else document.querySelectorAll(`meta[property="${property}"]`).forEach(element => element.remove());
    }
    if (ogImageAlt) updateMetaTag('meta[name="twitter:image:alt"]', 'content', ogImageAlt);
    else document.querySelectorAll('meta[name="twitter:image:alt"]').forEach(element => element.remove());
    if (ogVideo) {
      updateMetaTag('meta[property="og:video"]', 'content', ogVideo);
      // Ensure secure_url is also set for HTTPS compatibility
      updateMetaTag('meta[property="og:video:secure_url"]', 'content', ogVideo);
    } else {
      document.querySelectorAll('meta[property^="og:video"]').forEach((element) => element.remove());
    }
    if (ogVideoType) {
      updateMetaTag('meta[property="og:video:type"]', 'content', ogVideoType);
    }
    if (ogVideoWidth) {
      updateMetaTag('meta[property="og:video:width"]', 'content', ogVideoWidth);
    }
    if (ogVideoHeight) {
      updateMetaTag('meta[property="og:video:height"]', 'content', ogVideoHeight);
    }
    if (ogUrl) {
      updateMetaTag('meta[property="og:url"]', 'content', ogUrl);
    }

    // Update Twitter Card tags
    if (twitterCard) {
      updateMetaTag('meta[name="twitter:card"]', 'content', twitterCard);
    }
    if (ogTitle || title) {
      updateMetaTag('meta[name="twitter:title"]', 'content', ogTitle || title!);
    }
    if (ogDescription || description) {
      updateMetaTag('meta[name="twitter:description"]', 'content', ogDescription || description!);
    }
    if (ogImage) {
      updateMetaTag('meta[name="twitter:image"]', 'content', ogImage);
    }
    if (twitterPlayer) {
      updateMetaTag('meta[name="twitter:player"]', 'content', twitterPlayer);
    } else {
      document.querySelectorAll('meta[name^="twitter:player"]').forEach((element) => element.remove());
    }
    if (twitterPlayerWidth) {
      updateMetaTag('meta[name="twitter:player:width"]', 'content', twitterPlayerWidth);
    }
    if (twitterPlayerHeight) {
      updateMetaTag('meta[name="twitter:player:height"]', 'content', twitterPlayerHeight);
    }

    // Update canonical URL (strict domain + clean pathname)
    let linkElement = document.querySelector('link[rel="canonical"]') as HTMLLinkElement;
    if (!linkElement) {
      linkElement = document.createElement('link');
      linkElement.setAttribute('rel', 'canonical');
      document.head.appendChild(linkElement);
    }
    linkElement.setAttribute('href', finalCanonicalUrl);

    // Add structured data
    if (structuredData) {
      const scriptId = 'structured-data-script';
      const scriptElement = document.getElementById(scriptId) as HTMLScriptElement | null;

      if (scriptElement) {
        scriptElement.textContent = JSON.stringify(structuredData);
      } else {
        const newScriptElement = document.createElement('script') as HTMLScriptElement;
        newScriptElement.id = scriptId;
        newScriptElement.type = 'application/ld+json';
        newScriptElement.textContent = JSON.stringify(structuredData);
        document.head.appendChild(newScriptElement);
      }
    } else {
      document.getElementById('structured-data-script')?.remove();
    }

    // Update hreflang alternate links
    document.querySelectorAll('link[data-seo-hreflang="true"]').forEach((el) => el.remove());
    if (hreflangLinks && hreflangLinks.length > 0) {
      hreflangLinks.forEach(({ hreflang, href }) => {
        const altLink = document.createElement('link');
        altLink.setAttribute('rel', 'alternate');
        altLink.setAttribute('hreflang', hreflang);
        altLink.setAttribute('href', href);
        altLink.setAttribute('data-seo-hreflang', 'true');
        document.head.appendChild(altLink);
      });
    }

    // Handle Image Preloading
    if (preloadImages) {
      preloadImages.forEach((img) => {
        const linkId = `preload-${img.href.replace(/[^a-zA-Z0-9]/g, '-')}`;
        if (!document.getElementById(linkId)) {
          const link = document.createElement('link');
          link.id = linkId;
          link.rel = 'preload';
          link.as = 'image';
          link.href = img.href;
          if (img.media) link.media = img.media;
          document.head.appendChild(link);
        }
      });
    }

    return () => {
      document.querySelectorAll('link[data-seo-hreflang="true"]').forEach((el) => el.remove());
      document.querySelectorAll('meta[data-seo-alternate="true"]').forEach((el) => el.remove());
    };
  }, [title, description, keywords, ogTitle, ogDescription, ogImage, ogImageAlt, ogImageWidth, ogImageHeight, ogVideo, ogVideoType, ogVideoWidth, ogVideoHeight, ogUrl, ogLocale, ogLocaleAlternates, hreflangLinks, twitterCard, twitterPlayer, twitterPlayerWidth, twitterPlayerHeight, canonicalUrl, structuredData, noIndex, noIndexFollow, preloadImages, location.pathname]);

  return null; // This component doesn't render anything
};

export default SEOHead;
